/**
 * The person form's schemas, value shapes and pure conversions — the edited
 * person (`PersonDetailWire`) → form values, and form values → the
 * `create_person` / `update_person` payload. No React; unit-testable.
 *
 * Three react-hook-form forms back the dialog:
 * - the PERSON form (`createPersonFormSchema(t)`) — every scalar the RPCs
 *   write, including `person_type` and `status`;
 * - the BUSINESS form — the identity written to the person's own
 *   `businesses` row when "is a business" is ticked (a follow-up request);
 * - the DEI form — admins only (a follow-up request).
 *
 * The four child sets (disciplines, skills, locations, emergency contacts)
 * are drafts in component state, each with a user-edit flag: an edit sends
 * only the sets the user touched, because the RPC REPLACES a present set
 * (deleting and re-inserting its rows).
 */
import { z } from "zod";
import {
  PERSON_ARCHIVE_ONLY_COUNT_KEYS,
  type PersonDetailWire,
  type PersonLocationType,
  type PersonProficiencyLevel,
} from "@releve/shared";
import type { CreatePersonData } from "@/types/people";

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
// Person form
// ---------------------------------------------------------------------------

const isEmail = (value: string) => z.string().email().safeParse(value).success;
const isUrl = (value: string) => z.string().url().safeParse(value).success;

export const createPersonFormSchema = (t: TranslateFn) =>
  z.object({
    first_name: z.string().trim().min(1, t("people:form.validation.firstNameRequired")),
    last_name: z.string().trim().min(1, t("people:form.validation.lastNameRequired")),
    middle_name: z.string(),
    preferred_name: z.string(),
    display_name: z.string(),
    email: z.string().refine((v) => !v.trim() || isEmail(v.trim()), t("people:form.validation.invalidEmail")),
    secondary_email: z
      .string()
      .refine((v) => !v.trim() || isEmail(v.trim()), t("people:form.validation.invalidEmail")),
    phone: z.string(),
    mobile_phone: z.string(),
    work_phone: z.string(),
    company: z.string(),
    job_title: z.string(),
    department: z.string(),
    person_type: z.array(z.string()),
    status: z.string().min(1, t("people:form.validation.statusRequired")),
    has_system_access: z.boolean(),
    is_business: z.boolean(),
    linkedin_url: z.string().refine((v) => !v.trim() || isUrl(v.trim()), t("people:form.validation.invalidUrl")),
    website: z.string().refine((v) => !v.trim() || isUrl(v.trim()), t("people:form.validation.invalidUrl")),
    bio: z.string(),
    notes: z.string(),
  });

export type PersonFormValues = z.infer<ReturnType<typeof createPersonFormSchema>>;

export function personFormDefaults(
  person?: PersonDetailWire | null,
  defaultPersonTypes?: string[],
): PersonFormValues {
  return {
    first_name: person?.first_name ?? "",
    last_name: person?.last_name ?? "",
    middle_name: person?.middle_name ?? "",
    preferred_name: person?.preferred_name ?? "",
    display_name: person?.display_name ?? "",
    email: person?.email ?? "",
    secondary_email: person?.secondary_email ?? "",
    phone: person?.phone ?? "",
    mobile_phone: person?.mobile_phone ?? "",
    work_phone: person?.work_phone ?? "",
    company: person?.company ?? "",
    job_title: person?.job_title ?? "",
    department: person?.department ?? "",
    person_type: person ? person.person_type ?? [] : defaultPersonTypes ?? [],
    status: person?.status || "active",
    has_system_access: person?.has_system_access ?? false,
    is_business: person?.is_business ?? false,
    linkedin_url: person?.linkedin_url ?? "",
    website: person?.website ?? "",
    bio: person?.bio ?? "",
    notes: person?.notes ?? "",
  };
}

// ---------------------------------------------------------------------------
// Business identity (written to the person's own `businesses` row)
// ---------------------------------------------------------------------------

export const businessIdentitySchema = z.object({
  business_name: z.string(),
  trading_name: z.string(),
  country_code: z.string(),
  legal_form_id: z.string().nullable(),
  registration_number: z.string(),
  vat_number: z.string(),
  iban: z.string(),
  bic: z.string(),
});

export type BusinessIdentityFormValues = z.infer<typeof businessIdentitySchema>;

export const EMPTY_BUSINESS_IDENTITY: BusinessIdentityFormValues = {
  business_name: "",
  trading_name: "",
  country_code: "",
  legal_form_id: null,
  registration_number: "",
  vat_number: "",
  iban: "",
  bic: "",
};

// ---------------------------------------------------------------------------
// DEI (org admins only)
// ---------------------------------------------------------------------------

export const deiFormSchema = z.object({
  gender: z.string(),
  pronouns: z.string(),
  ethnicity: z.array(z.string()),
  race: z.array(z.string()),
  sexual_orientation: z.string(),
  disability_status: z.string(),
  veteran_status: z.string(),
  age_range: z.string(),
  primary_language: z.string(),
  religious_affiliation: z.string(),
  socioeconomic_background: z.string(),
  dei_data_consent: z.boolean(),
});

export type DeiFormValues = z.infer<typeof deiFormSchema>;

export function deiFormDefaults(dei?: PersonDetailWire["dei"]): DeiFormValues {
  return {
    gender: dei?.gender ?? "",
    pronouns: dei?.pronouns ?? "",
    ethnicity: dei?.ethnicity ?? [],
    race: dei?.race ?? [],
    sexual_orientation: dei?.sexual_orientation ?? "",
    disability_status: dei?.disability_status ?? "",
    veteran_status: dei?.veteran_status ?? "",
    age_range: dei?.age_range ?? "",
    primary_language: dei?.primary_language ?? "",
    religious_affiliation: dei?.religious_affiliation ?? "",
    socioeconomic_background: dei?.socioeconomic_background ?? "",
    dei_data_consent: dei?.dei_data_consent ?? false,
  };
}

// ---------------------------------------------------------------------------
// Child-set drafts
// ---------------------------------------------------------------------------

export interface DisciplineDraft {
  discipline_id: string;
  /** Display fallback until the vocabulary query has the row. */
  name: string;
  start_date: string | null;
  end_date: string | null;
  is_primary: boolean;
  notes: string;
}

export interface SkillDraft {
  skill_id: string;
  name: string;
  category: string;
  proficiency_level: PersonProficiencyLevel | null;
  years_experience: number | null;
  last_used: string | null;
  notes: string;
}

export interface LocationDraft {
  /** Stable React key — the row id, or a temporary key for a new location. */
  key: string;
  gis_location_id: string;
  formatted_address: string;
  location_type: PersonLocationType;
  is_primary: boolean;
  notes: string;
}

export interface EmergencyContactDraft {
  key: string;
  contact_name: string;
  relationship: string;
  phone: string;
  email: string;
  is_primary: boolean;
}

export interface PersonChildDrafts {
  disciplines: DisciplineDraft[];
  skills: SkillDraft[];
  locations: LocationDraft[];
  emergency_contacts: EmergencyContactDraft[];
}

export type PersonChildTouched = Record<keyof PersonChildDrafts, boolean>;

export const UNTOUCHED: PersonChildTouched = {
  disciplines: false,
  skills: false,
  locations: false,
  emergency_contacts: false,
};

let tempKeySeq = 0;
/** A client-only key for a row that has no id yet. */
export function tempKey(prefix: string): string {
  tempKeySeq += 1;
  return `${prefix}-new-${tempKeySeq}`;
}

/**
 * `person_disciplines` may hold the same discipline more than once (a global
 * row plus contextual rows — `unique_person_role_global` /
 * `unique_person_role_context`). The form cannot express context and
 * `update_person` refuses a duplicate `discipline_id`, so keep ONE row per
 * discipline: the primary one first, then the global (context-less) one, then
 * the first in load order. A save therefore collapses a contextual set to one
 * global row — the plan's accepted consequence of dropping context.
 */
function dedupeDisciplineRows(
  rows: NonNullable<PersonDetailWire["disciplines"]>,
): NonNullable<PersonDetailWire["disciplines"]> {
  const rank = (row: (typeof rows)[number]): number =>
    (row.is_primary ? 0 : 2) + (row.context_id ? 1 : 0);
  const ordered = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !!row.discipline_id)
    .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index);
  const seen = new Set<string>();
  const kept = new Set<number>();
  for (const { row, index } of ordered) {
    const id = row.discipline_id as string;
    if (seen.has(id)) continue;
    seen.add(id);
    kept.add(index);
  }
  // Keep the person's original order among the survivors.
  return rows.filter((_, index) => kept.has(index));
}

export function childDraftsFromPerson(person?: PersonDetailWire | null): PersonChildDrafts {
  if (!person) return { disciplines: [], skills: [], locations: [], emergency_contacts: [] };
  return {
    disciplines: dedupeDisciplineRows(person.disciplines ?? []).map((row) => ({
        discipline_id: row.discipline_id as string,
        name: row.discipline?.name ?? "",
        start_date: row.start_date,
        end_date: row.end_date,
        is_primary: Boolean(row.is_primary),
        notes: row.notes ?? "",
      })),
    skills: (person.skills ?? [])
      .filter((row) => !!row.skill_id)
      .map((row) => ({
        skill_id: row.skill_id as string,
        name: row.skill?.name ?? "",
        category: row.skill?.category ?? "",
        proficiency_level: row.proficiency_level,
        years_experience: row.years_experience,
        last_used: row.last_used,
        notes: row.notes ?? "",
      })),
    locations: (person.locations ?? []).map((row) => ({
      key: row.id,
      gis_location_id: row.gis_location_id,
      formatted_address: row.gis_location?.formatted_address ?? "",
      location_type: row.location_type,
      is_primary: row.is_primary,
      notes: row.notes ?? "",
    })),
    emergency_contacts: (person.emergency_contacts ?? []).map((row) => ({
      key: row.id,
      contact_name: row.contact_name,
      relationship: row.relationship ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      is_primary: Boolean(row.is_primary),
    })),
  };
}

/** Per-row inline errors for the emergency contacts (keyed by draft key). */
export type EmergencyContactErrors = Record<string, { contact_name?: string; email?: string }>;

/**
 * A row with no name is a blank row the user never filled — it is dropped on
 * save, unless it carries other data, in which case the name is required.
 */
export function validateEmergencyContacts(
  contacts: EmergencyContactDraft[],
  t: TranslateFn,
): EmergencyContactErrors {
  const errors: EmergencyContactErrors = {};
  for (const contact of contacts) {
    const hasOther = !!(contact.relationship.trim() || contact.phone.trim() || contact.email.trim());
    const rowErrors: { contact_name?: string; email?: string } = {};
    if (!contact.contact_name.trim() && hasOther) {
      rowErrors.contact_name = t("people:form.validation.contactNameRequired");
    }
    if (contact.email.trim() && !isEmail(contact.email.trim())) {
      rowErrors.email = t("people:form.validation.invalidEmail");
    }
    if (rowErrors.contact_name || rowErrors.email) errors[contact.key] = rowErrors;
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

const orUndefined = (value: string) => (value.trim() ? value.trim() : undefined);

/**
 * The create / update body. Every scalar is sent (an emptied field is `''`,
 * which the shared schema folds to NULL, so clearing a field clears the
 * column). Child sets: a CREATE sends all four; an EDIT sends only the sets
 * the user touched — the RPC replaces a present set wholesale, so an
 * untouched set is left out and its rows (and their `created_at`, and any
 * discipline context the form cannot express) stay as they are.
 */
export function buildPersonPayload(
  values: PersonFormValues,
  drafts: PersonChildDrafts,
  touched: PersonChildTouched | "all",
): CreatePersonData {
  const payload: CreatePersonData = {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    middle_name: values.middle_name,
    preferred_name: values.preferred_name,
    display_name: values.display_name,
    email: values.email.trim(),
    secondary_email: values.secondary_email.trim(),
    phone: values.phone,
    mobile_phone: values.mobile_phone,
    work_phone: values.work_phone,
    company: values.company,
    job_title: values.job_title,
    department: values.department,
    person_type: values.person_type,
    status: values.status,
    has_system_access: values.has_system_access,
    is_business: values.is_business,
    linkedin_url: values.linkedin_url.trim(),
    website: values.website.trim(),
    bio: values.bio,
    notes: values.notes,
  };
  const include = (key: keyof PersonChildDrafts) => touched === "all" || touched[key];
  if (include("disciplines")) {
    payload.disciplines = drafts.disciplines.map((d) => ({
      discipline_id: d.discipline_id,
      start_date: d.start_date ?? undefined,
      end_date: d.end_date ?? undefined,
      is_primary: d.is_primary,
      notes: orUndefined(d.notes),
    }));
  }
  if (include("skills")) {
    payload.skills = drafts.skills.map((s) => ({
      skill_id: s.skill_id,
      proficiency_level: s.proficiency_level ?? undefined,
      years_experience: s.years_experience ?? undefined,
      last_used: s.last_used ?? undefined,
      notes: orUndefined(s.notes),
    }));
  }
  if (include("locations")) {
    payload.locations = drafts.locations.map((l) => ({
      gis_location_id: l.gis_location_id,
      location_type: l.location_type,
      is_primary: l.is_primary,
      notes: orUndefined(l.notes),
    }));
  }
  if (include("emergency_contacts")) {
    payload.emergency_contacts = drafts.emergency_contacts
      .filter((c) => c.contact_name.trim())
      .map((c) => ({
        contact_name: c.contact_name.trim(),
        relationship: orUndefined(c.relationship),
        phone: orUndefined(c.phone),
        email: orUndefined(c.email),
        is_primary: c.is_primary,
      }));
  }
  return payload;
}

// ---------------------------------------------------------------------------
// Display helpers shared by the form sections
// ---------------------------------------------------------------------------

/**
 * The stored DEI answers are English vocabulary strings; the label shown is
 * the translation keyed by a slug of the stored value (falling back to the
 * value itself), so the data never changes with the viewer's language.
 */
export const deiOptionSlug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

/**
 * "Referenced by 3 expenses, 1 AP bill" — the archive-only reasons as one
 * sentence fragment, in the shared display order.
 */
export function describeArchiveReasons(t: TranslateFn, person: PersonDetailWire): string {
  const reasons = person.archive_only_reasons;
  const parts = PERSON_ARCHIVE_ONLY_COUNT_KEYS.filter((key) => reasons[key] > 0).map((key) =>
    t(`people:dangerZone.reasons.${key}`, { count: reasons[key] }),
  );
  return parts.length > 0 ? t("people:dangerZone.referencedBy", { list: parts.join(", ") }) : "";
}
